import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { ArrowRight, Download, ImageDown, Loader2 } from "lucide-react";
import "@/styles/campaign-kit.css";

/* ------------------------------------------------------------------
   8 social-post creatives — Square 1080×1080, RUAM-inspired editorial
   style, premium minimalist legal brand.

   Global art direction (per brief):
   • Typography: elegant serif (Playfair) + thin sans
   • Palette: dark charcoal, soft ivory, warm gold
   • Mood: calm, confident, understated luxury
   • Layout: lots of negative space, balanced
------------------------------------------------------------------ */

const POSTS = [
  {
    id: "clarity",
    theme: "ivory-image",
    label: "Clarity",
    headline: "Do you actually need a lawyer yet?",
    subtext: "Most people don't know where they stand before they start.",
    cta: "Get clarity",
    href: "/calculators",
    image: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1600&q=80",
    direction: "Calm modern interior with documents on a desk; soft natural light; neutral beiges.",
  },
  {
    id: "risk",
    theme: "dark-image",
    label: "Risk",
    headline: "What happens if you get it wrong?",
    subtext: "Legal mistakes are rarely obvious at the start.",
    cta: "Check your situation",
    href: "/intake",
    image: "https://images.unsplash.com/photo-1493863641943-9b68992a8d07?auto=format&fit=crop&w=1600&q=80",
    direction: "Cinematic dusk skyline; moody contrast; serious but calm.",
  },
  {
    id: "cost",
    theme: "ivory-image",
    label: "Cost clarity",
    headline: "What will this really cost?",
    subtext: "Before you start, understand the real cost — and what's required.",
    cta: "See your costs",
    href: "/calculators",
    image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1600&q=80",
    direction: "High-end desk scene: calculator, notebook, papers; soft overhead light; gold accents.",
  },
  {
    id: "no-pressure",
    theme: "solid-ivory",
    label: "No pressure",
    headline: "You don't need to commit to check.",
    subtext: "No calls. No pressure. Just clarity on where you stand.",
    cta: "Get started",
    href: "/intake",
    direction: "Ultra-minimal ivory ground. Typography-led. Editorial.",
  },
  {
    id: "control",
    theme: "ivory-image",
    label: "Control",
    headline: "Most legal processes feel out of your control.",
    subtext: "We guide you step-by-step so you know exactly what's happening.",
    cta: "Start with clarity",
    href: "/intake",
    image: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1600&q=80",
    direction: "Calm home workspace; open laptop & papers; soft daylight; organised.",
  },
  {
    id: "marriage",
    theme: "ivory-image",
    label: "Marriage",
    headline: "Getting married soon?",
    subtext: "Choosing the wrong contract now can cost you later.",
    cta: "Check what's right for you",
    href: "/intake",
    image: "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1600&q=80",
    direction: "Elegant lifestyle frame of two people, soft & bright; faces away; modern romantic neutrals.",
  },
  {
    id: "peace",
    theme: "ivory-image",
    label: "Peace of mind",
    headline: "If something happened tomorrow… would everything be in order?",
    subtext: "A will is not paperwork — it's peace of mind.",
    cta: "See what's required",
    href: "/intake",
    image: "https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e?auto=format&fit=crop&w=1600&q=80",
    direction: "Soft emotional still life — frame, plant, empty chair; subtle, not dramatic.",
  },
  {
    id: "legacy",
    theme: "dark-image",
    label: "Legacy",
    headline: "A trust isn't just for wealth.",
    subtext: "It's about protecting what matters — now and later.",
    cta: "Explore your options",
    href: "/intake",
    image: "https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=1600&q=80",
    direction: "Modern minimalist interior, plant or architectural detail; soft shadows; timeless.",
  },
];

export default function CampaignKit() {
  // Map of post.id → DOM node (the .ck-card element) for PNG export.
  const cardRefs = useRef({});
  const [bulkBusy, setBulkBusy] = useState(false);

  const registerCard = (id, node) => {
    if (node) cardRefs.current[id] = node;
    else delete cardRefs.current[id];
  };

  const downloadOne = async (postId) => {
    const node = cardRefs.current[postId];
    if (!node) return;
    await exportCardToPng(node, postId);
  };

  const downloadAll = async () => {
    setBulkBusy(true);
    try {
      for (const p of POSTS) {
        const node = cardRefs.current[p.id];
        if (!node) continue;
        // Small gap between triggers so browsers don't merge / suppress
        // sequential download dialogs.
        // eslint-disable-next-line no-await-in-loop
        await exportCardToPng(node, p.id);
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 350));
      }
      toast.success("All 8 posts downloaded", {
        description: "Check your browser's Downloads folder.",
      });
    } catch (e) {
      toast.error("Download all failed", { description: e?.message || "" });
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="p-10 max-w-7xl mx-auto" data-testid="campaign-kit">
      <div className="mb-10 flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="text-overline text-zinc-500">Campaign kit</div>
          <h1 className="font-serif text-4xl lg:text-5xl font-medium tracking-tight mt-2">
            8 social posts — production-ready
          </h1>
          <p className="text-sm text-zinc-500 mt-3 max-w-2xl">
            Premium editorial creatives for LinkedIn, Instagram & Facebook.
            Square 1080×1080, ivory / charcoal / warm-gold palette, serif
            display type, lots of negative space. Click <strong>Download PNG</strong>
            {" "}on any card to export, or grab the lot in one go.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={downloadAll}
            disabled={bulkBusy}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider font-medium bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50"
            data-testid="campaign-download-all-btn"
          >
            {bulkBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImageDown className="h-3 w-3" />}
            {bulkBusy ? "Exporting…" : "Download all (8 PNGs)"}
          </button>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider font-medium border border-zinc-300 text-zinc-700 hover:bg-zinc-50"
            data-testid="campaign-print-btn"
          >
            <Download className="h-3 w-3" /> Print sheet
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {POSTS.map((p, i) => (
          <SocialPost
            key={p.id}
            post={p}
            index={i + 1}
            registerCard={registerCard}
            onDownload={() => downloadOne(p.id)}
          />
        ))}
      </div>

      <div className="mt-16 border-t border-zinc-200 pt-8">
        <div className="text-overline text-zinc-500 mb-3">Campaign flow</div>
        <p className="text-sm text-zinc-600 leading-relaxed max-w-3xl">
          Each post drives a single curiosity-first click → website → 
          <Link to="/intake" className="underline mx-1">Get started</Link>
          → captured as a lead in the admin Leads board. Posts are designed
          to create curiosity <em>before</em> offering a solution, so the
          calculator or guided intake feels like a natural next step rather
          than a sales pitch.
        </p>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// PNG export — captures the .ck-card node at exactly 1080×1080. Computes
// pixelRatio so the output stays 1080×1080 regardless of the on-screen
// rendered size. Waits for fonts + Unsplash image to settle first.
// ---------------------------------------------------------------------------
const TARGET_PX = 1080;

async function exportCardToPng(node, slug) {
  try {
    // Make sure custom fonts (Playfair, Manrope) are loaded — otherwise the
    // captured PNG renders in the fallback serif.
    if (document.fonts && document.fonts.ready) {
      await document.fonts.ready;
    }
    // Wait for any background image inside the card to actually paint.
    const imgEl = node.querySelector(".ck-img");
    if (imgEl) {
      const url = (imgEl.style.backgroundImage.match(/url\(["']?([^"')]+)["']?\)/) || [])[1];
      if (url) {
        await preloadImage(url);
      }
    }
    // Compute pixelRatio so we always export at the target square resolution.
    const rect = node.getBoundingClientRect();
    const baseW = rect.width || TARGET_PX;
    const pixelRatio = TARGET_PX / baseW;

    const dataUrl = await toPng(node, {
      cacheBust: true,
      pixelRatio,
      width: baseW,
      height: baseW,            // .ck-card is aspect-ratio: 1/1
      backgroundColor: undefined,
      style: { borderRadius: "0" },
      // Bypass cross-origin tainting; Unsplash sends ACAO so this is safe.
      fetchRequestInit: { mode: "cors" },
    });

    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `eh-inc-campaign-${slug}-1080.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("PNG ready", { description: `${slug} · 1080 × 1080` });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("PNG export failed", err);
    toast.error("Couldn't export PNG", { description: err?.message || "" });
  }
}

function preloadImage(url) {
  return new Promise((resolve) => {
    const i = new Image();
    i.crossOrigin = "anonymous";
    i.onload = resolve;
    i.onerror = resolve;
    i.src = url;
  });
}

/* ================================================================== */
/* Single 1080×1080 post                                              */
/* ================================================================== */

function SocialPost({ post, index, registerCard, onDownload }) {
  const [busy, setBusy] = useState(false);
  const dark = post.theme === "dark-image" || post.theme === "solid-charcoal";
  const useImage = post.theme === "ivory-image" || post.theme === "dark-image";
  const veilClass = {
    "ivory-image":     "ck-veil--ivory",
    "dark-image":      "ck-veil--dark",
    "solid-ivory":     "ck-veil--solid-ivory",
    "solid-charcoal":  "ck-veil--solid-charcoal",
  }[post.theme];

  const handleDownload = async () => {
    setBusy(true);
    try { await onDownload(); } finally { setBusy(false); }
  };

  return (
    <figure className="flex flex-col gap-3" data-testid={`post-card-${post.id}`}>
      <div
        ref={(el) => registerCard(post.id, el)}
        className={`ck-card ${dark ? "ck-card--dark" : ""}`}
      >
        {useImage && (
          <div className="ck-img" style={{ backgroundImage: `url(${post.image})` }} />
        )}
        <div className={`ck-veil ${veilClass}`} />
        <div className="ck-corner-rule" aria-hidden="true" />

        <div className="ck-content">
          {/* TOP: label */}
          <div className="ck-sans ck-label">{post.label}</div>

          {/* MIDDLE: headline + subtext */}
          <div className="flex-1 flex flex-col justify-center -mt-[2%]">
            <h2 className="ck-serif ck-headline">{post.headline}</h2>
            <p className="ck-sans ck-subtext">{post.subtext}</p>
          </div>

          {/* BOTTOM: CTA + brand */}
          <div className="flex items-end justify-between gap-4">
            <span className="ck-sans ck-cta">
              {post.cta} <ArrowRight className="h-3 w-3" />
            </span>
            <div className="ck-sans ck-brand">
              EH Inc
              <span className="ck-brand-mark">Attorneys &amp; Conveyancers</span>
            </div>
          </div>
        </div>
      </div>

      <figcaption className="px-1 flex items-baseline justify-between gap-4 text-xs">
        <div>
          <span className="text-zinc-400 mr-2">{String(index).padStart(2, "0")}</span>
          <span className="font-medium text-zinc-900">{post.label}</span>
          <span className="text-zinc-500 ml-2">— {post.id}</span>
        </div>
        <div className="text-zinc-400 italic max-w-[55%] text-right leading-snug">
          {post.direction}
        </div>
      </figcaption>

      <button
        onClick={handleDownload}
        disabled={busy}
        className="self-start inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-wider font-medium border border-zinc-300 text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
        data-testid={`post-download-${post.id}`}
      >
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImageDown className="h-3 w-3" />}
        {busy ? "Exporting…" : "Download PNG"}
      </button>
    </figure>
  );
}
